/**
 * Trace a number in a tab spec table: which engine table a cell of the
 * finished table came from, the cell's leaves under the labels the engine
 * gave them, and what a trace calls the names the tab spec's compiler makes.
 * A finished table is put together from several engine tables (the main
 * XTAB, the mean DTABs, a table per sibling of a grid); the cell's row says
 * which. Pure.
 */

import type { AxisLeaf } from "utils/tableModel"
import type { TraceRequest } from "features/Provenance/utils/traceTable"
import type { RowSlot, TablePlan } from "../compiler/table"
import { safeId, TableNames, tableNames, TOTAL_PREP, TOTAL_TABLE } from "../compiler/names"

const BASE_LABEL = "Base"

/** The part of a sibling table the cell's row belongs to, with its engine names. */
const siblingOf = (
  plan: TablePlan,
  down: AxisLeaf
): { names: TableNames; layout: RowSlot[]; display: string; matched: boolean } | null => {
  const parts = plan.siblings?.parts ?? []
  if (parts.length === 0) return null
  // The k-th part's tables are the k-th sibling main XTAB of the script.
  const id = safeId(plan.tableId)
  const mains = [...plan.script.matchAll(new RegExp(`^\\s*XTAB\\s+ts_${id}_s(\\d+)\\s`, "gm"))].map(
    (m) => m[1]
  )
  const at = parts.findIndex((p) => down.path.includes(p.display))
  const index = at < 0 ? 0 : at
  const n = mains[index]
  if (n === undefined) return null
  return {
    names: tableNames(`${plan.tableId}_s${n}`),
    layout: parts[index].layout,
    display: parts[index].display,
    matched: at >= 0,
  }
}

/** The across leaf under the banner's engine labels. */
const engineAcross = (plan: TablePlan, across: AxisLeaf): AxisLeaf => {
  const path = across.path
  const column = plan.banner?.columns.find((c) =>
    path.length >= 2
      ? c.groupDisplay === path[path.length - 2] && c.display === path[path.length - 1]
      : c.display === path[0]
  )
  return column ? { ...across, path: [column.group, column.label] } : across
}

/**
 * What to trace for the cell (`down`, `across`) of the finished table of
 * `plan`: the engine table, the leaves as the engine labels them, whether
 * the engine table runs the banner down (the mean DTABs take the banner
 * first), and a note when the trace follows only part of what made the
 * number.
 */
export const tabSpecTraceTarget = (
  plan: TablePlan,
  down: AxisLeaf,
  across: AxisLeaf
): Pick<TraceRequest, "table" | "down" | "across" | "transposed" | "note" | "tableScript"> => {
  const sibling = siblingOf(plan, down)
  const names = sibling?.names ?? tableNames(plan.tableId)
  const layout = sibling?.layout ?? plan.layout
  const notes: string[] = []
  if (plan.siblings && !sibling)
    notes.push("This table puts together a table for each row of a grid; the trace follows its main table.")
  else if (sibling && !sibling.matched)
    notes.push(
      `This table puts together a table for each row of a grid; the trace follows the one for "${sibling.display}".`
    )

  const shown = sibling ? down.path.filter((p) => p !== sibling.display) : down.path
  const label = shown[shown.length - 1]
  const engineCross = engineAcross(plan, across)
  const main = {
    table: names.result,
    tableScript: plan.script,
    down: { ...down, path: shown },
    across: engineCross,
  }
  const mean = (excludingZero: boolean) => ({
    table: across.isTotal
      ? excludingZero
        ? names.meanExcl0Total
        : names.meanTotal
      : excludingZero
        ? names.meanExcl0
        : names.mean,
    tableScript: plan.script,
    down: { ...down, path: shown },
    across: engineCross,
    transposed: true,
  })
  const withNotes = <T extends object>(target: T) =>
    notes.length > 0 ? { ...target, note: notes.join(" ") } : target

  // The Base rows are the Totals of the main table (or the statistics' own base).
  if (label === BASE_LABEL) {
    const stats = layout.some((s) => s.kind === "base" && s.stats)
    const target = stats ? mean(false) : main
    return withNotes({ ...target, down: { ...target.down, isTotal: true } })
  }
  const slot = layout.find((s) => s.kind !== "base" && s.display === label)
  if (slot?.kind === "row")
    return withNotes({ ...main, down: { ...main.down, path: [...shown.slice(0, -1), slot.label] } })
  if (slot?.kind === "stat") return withNotes(mean(slot.excludingZero === true))
  if (slot?.kind === "difference") {
    if (slot.fields) return withNotes(mean(false))
    notes.push(
      `This row is worked out here from "${slot.plus}" and "${slot.minus}"; the trace shows the table they come from.`
    )
  }
  return withNotes(main)
}

/** The tab spec's own names, for a trace: its helpers, the Total table and the tables. */
export const describeTabSpecHelper = (
  titles: Map<string, string>
): ((name: string) => string | undefined) => {
  // Longest first, so "ts_<id>_m" is not taken for table "<id>_m".
  const ids = [...titles.keys()]
    .map((id) => [safeId(id), id] as const)
    .sort((a, b) => b[0].length - a[0].length)
  const tableOf = (rest: string): { title: string; suffix: string } | null => {
    for (const [safe, id] of ids) {
      if (rest === safe || rest.startsWith(`${safe}_`))
        return { title: titles.get(id) ?? "", suffix: rest.slice(safe.length) }
    }
    return null
  }
  const ofTable = (what: string, rest: string): string => {
    const found = tableOf(rest)
    return found?.title ? `${what} of "${found.title}"` : `${what} of a table`
  }
  return (name) => {
    if (name === TOTAL_TABLE) return "Total (everyone)"
    if (name.startsWith("__ts_b_")) return "Banner"
    if (name.startsWith("__ts_r_")) return ofTable("Rows", name.slice(7))
    if (name.startsWith("__ts_ub_")) return ofTable("Base (who answered a row)", name.slice(8))
    if (name.startsWith("__ts_u_")) return ofTable("Base", name.slice(7))
    if (name.startsWith("__ts_sc_")) return ofTable("Scores", name.slice(8))
    if (name.startsWith("__ts_s_")) return ofTable("Score", name.slice(7))
    if (name.startsWith("__ts_z_")) return ofTable("Values without 0", name.slice(7))
    if (name.startsWith("__ts_x_")) return ofTable("Answer check", name.slice(7))
    if (name.startsWith("__ts_")) return "Tab spec helper"
    if (name.startsWith("ts_")) {
      const found = tableOf(name.slice(3))
      if (!found) return undefined
      const what = /_m(z)?t?$/.test(found.suffix) ? "Mean table" : "Table"
      return found.title ? `${what} of "${found.title}"` : `${what} of a table`
    }
    return undefined
  }
}

/** The name of a script the tab spec sent: the Total set-up, a banner, another table's script. */
export const describeTabSpecScript = (
  titles: Map<string, string>
): ((script: string) => string | undefined) => {
  const describe = describeTabSpecHelper(titles)
  return (script) => {
    if (script === TOTAL_PREP.trim()) return "Total column set-up"
    if (/^\s*QTABLE\s+__ts_b_/.test(script)) return "Banner"
    const table = /^\s*XTAB\s+(ts_\S+)/m.exec(script)?.[1]
    if (!table) return undefined
    const name = describe(table)
    return name ? name.replace(/^(Mean table|Table) of/, "Script of") : undefined
  }
}
